import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PaymentElementForm } from "@/components/PaymentElementForm";
import { X, Lock, ShieldCheck, Check } from "lucide-react";
import { mpInitiateCheckout } from "@/lib/metaPixel";

/**
 * Luxe checkout lightbox with an in-app, brand-styled Stripe Payment Element form.
 */
export function CheckoutLightbox({ open, onClose, kind, productId, payload = {}, title, lines = [], total, note, cta = "Continue to Secure Payment", successNote, onSuccess, onDone, onSchoolVerifyRequired }) {
  const [paying, setPaying] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  // Meta Pixel — InitiateCheckout
  useEffect(() => {
    if (!open) return;
    try {
      const cid = productId ? (payload?.color ? `${productId}:${payload.color}` : productId) : (kind || "checkout");
      mpInitiateCheckout({
        items: [{ id: cid, quantity: 1, item_price: Number(total) || 0 }],
        value: Number(total) || 0,
        num_items: 1,
        kind: kind || "checkout",
      });
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kind, productId, total]);

  if (!open) return null;
  const close = () => { setPaying(false); setDone(false); onClose(); };
  const complete = () => { setPaying(false); setDone(false); (onDone || onClose)(); };

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-4" onClick={close}>
      <div className="absolute inset-0 bg-ink/70 backdrop-blur-md" />
      <div data-testid="checkout-lightbox" className="relative bg-ivory rounded-sm max-w-md w-full max-h-[92vh] overflow-y-auto animate-fade-up shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="glass-dark text-ivory px-7 py-6 relative sticky top-0 z-10">
          <button onClick={close} className="absolute top-5 right-5 text-ivory/70 hover:text-ivory"><X className="w-5 h-5" /></button>
          <p className="font-label text-[9px] text-champagne mb-1.5">Reserve & Secure</p>
          <h3 className="font-display text-3xl">{title}</h3>
        </div>
        <div className="p-7">
          {done ? (
            <div data-testid="checkout-success" className="text-center py-6 space-y-4">
              <span className="w-14 h-14 mx-auto rounded-full bg-rosemetal/10 flex items-center justify-center"><Check className="w-7 h-7 text-rosemetal" /></span>
              <h4 className="font-display text-3xl">Payment confirmed</h4>
              <p className="font-body text-sm text-muted-foreground">{successNote || "You're all set — Heather has your order."}</p>
              <button data-testid="checkout-done-btn" onClick={complete} className="w-full bg-ink text-ivory py-4 font-label text-[10px] rounded-sm hover:bg-rosemetal transition-colors">Done</button>
            </div>
          ) : (
            <>
              <div className="space-y-3 mb-5">
                {lines.map((l, i) => (
                  <div key={i} className="flex justify-between font-body text-sm">
                    <span className="text-muted-foreground">{l.name}</span>
                    <span>{l.price}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between font-display text-2xl border-t border-border pt-4 mb-2">
                <span>Total</span><span>${total}</span>
              </div>
              {note && <p className="font-body text-xs text-rosemetal mb-5 flex items-start gap-1.5"><Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {note}</p>}

              {!paying ? (
                <>
                  <button data-testid="lightbox-proceed-btn" onClick={() => setPaying(true)} className="w-full bg-ink text-ivory py-4 font-label text-[10px] rounded-sm hover:bg-rosemetal transition-colors">
                    {cta}
                  </button>
                  <p className="font-label text-[8px] text-muted-foreground mt-4 flex items-center justify-center gap-1.5">
                    <ShieldCheck className="w-3 h-3" /> Encrypted card checkout powered by Stripe
                  </p>
                </>
              ) : (
                <div data-testid="embedded-checkout" className="mt-2">
                  <PaymentElementForm
                    payload={{ kind, product_id: productId, ...payload }}
                    cta={`Pay $${total}`}
                    onSuccess={(data) => { onSuccess && onSuccess(); if (data?.order_id) { close(); navigate(`/thank-you/${data.order_id}`); } else { setDone(true); } }}
                    onBack={() => setPaying(false)}
                    backLabel="Back to summary"
                    onSchoolVerifyRequired={() => { close(); onSchoolVerifyRequired && onSchoolVerifyRequired(); }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
